import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Input,
  Text,
  useToast,
  Select,
  VStack,
  Textarea,
  SimpleGrid,
  FormControl,
  FormLabel,
  Badge,
  Circle,
} from '@chakra-ui/react';
import { FiChevronDown, FiPlay, FiUpload, FiDownload, FiSave } from 'react-icons/fi';
import { TenantSettingsAPI } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import PageHelp from '../../components/PageHelp';
import { VoiceTestPlayground } from '../../components/voice/VoiceTestPlayground';
import { useLanguage } from '../../i18n';
import { getPortalPageCopy } from '../../portalPageCopy';
import BusinessHoursCard from '../../components/BusinessHoursCard';
import OwnerNotificationsCard from '../../components/OwnerNotificationsCard';

const DEFAULT_WELCOME_MESSAGE = 'Hello, thank you for calling. I am your AI assistant. How can I help you today?';

const COMMON_TIMEZONES = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'America/Los_Angeles',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Australia/Sydney',
  'Australia/Melbourne',
  'UTC',
];

function suggestTimezoneFromAddress(address) {
  if (!address || typeof address !== 'string') return null;
  const lower = address.trim().toLowerCase();
  if (lower.includes('china') || lower.includes('shanghai') || lower.includes('beijing') || lower.includes('中国')) return 'Asia/Shanghai';
  if (lower.includes('hong kong') || lower.includes('香港')) return 'Asia/Hong_Kong';
  if (lower.includes('japan') || lower.includes('tokyo') || lower.includes('東京')) return 'Asia/Tokyo';
  if (lower.includes('california') || lower.includes('los angeles') || lower.includes('san francisco') || lower.includes('la,') || lower.includes(' ca')) return 'America/Los_Angeles';
  if (lower.includes('new york') || lower.includes('ny,') || lower.includes('nyc')) return 'America/New_York';
  if (lower.includes('chicago') || lower.includes('illinois')) return 'America/Chicago';
  if (lower.includes('uk') || lower.includes('united kingdom') || lower.includes('london') || lower.includes('england')) return 'Europe/London';
  if (lower.includes('france') || lower.includes('paris')) return 'Europe/Paris';
  if (lower.includes('germany') || lower.includes('berlin')) return 'Europe/Berlin';
  if (lower.includes('australia') || lower.includes('sydney') || lower.includes('melbourne')) return 'Australia/Sydney';
  return null;
}

const SettingsPage = () => {
  const toast = useToast();
  const { tenantInfo } = useTenant();
  const { lang } = useLanguage();
  const copy = getPortalPageCopy(lang, 'settings');
  const setupSteps = [
    { key: 'profile', label: copy.profileStep, description: copy.profileStepDescription },
    { key: 'voice', label: copy.voiceStep, description: copy.voiceStepDescription },
    { key: 'context', label: copy.contextStep, description: copy.contextStepDescription },
  ];
  const companyFileInputRef = useRef(null);
  const stepScrollRef = useRef(null);

  // Company Info State
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
  });
  const [companyInfoLoading, setCompanyInfoLoading] = useState(false);
  const [companyInfoDirty, setCompanyInfoDirty] = useState(false);

  // AI Configuration State
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [voiceName, setVoiceName] = useState('Aoede');
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [timezone, setTimezone] = useState('Asia/Shanghai');
  const [hoursInfo, setHoursInfo] = useState({ hours: null, policy: null, openNow: undefined, sla: null });
  const [ownerNotifications, setOwnerNotifications] = useState(null);
  const [companyDescription, setCompanyDescription] = useState('');
  const [aiConfigLoading, setAiConfigLoading] = useState(false);
  const [aiConfigDirty, setAiConfigDirty] = useState(false);
  const [playgroundOpen, setPlaygroundOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [hasMoreStepContent, setHasMoreStepContent] = useState(false);

  const tenantId = tenantInfo?.uuid || localStorage.getItem('tenant_uuid');

  const suggestedTimezone = suggestTimezoneFromAddress(companyInfo.company_address);
  const canApplySuggestedTz = suggestedTimezone && suggestedTimezone !== timezone;
  const activeStepKey = setupSteps[activeStep]?.key || 'profile';

  const updateStepScrollHint = useCallback(() => {
    const node = stepScrollRef.current;
    if (!node) {
      setHasMoreStepContent(false);
      return;
    }
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;
    setHasMoreStepContent(remaining > 12);
  }, []);

  const scrollStepContentDown = () => {
    const node = stepScrollRef.current;
    if (!node) return;
    node.scrollBy({ top: Math.max(220, node.clientHeight * 0.45), behavior: 'smooth' });
  };

  useEffect(() => {
    const node = stepScrollRef.current;
    if (!node) return undefined;
    node.scrollTop = 0;
    const frame = window.requestAnimationFrame(updateStepScrollHint);
    const handleResize = () => updateStepScrollHint();
    window.addEventListener('resize', handleResize);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeStep, updateStepScrollHint]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(updateStepScrollHint);
    return () => window.cancelAnimationFrame(frame);
  }, [activeStep, welcomeMessage, voiceName, timezone, companyDescription, voiceOptions.length, updateStepScrollHint]);

  // Load all settings from TenantSettingsAPI
  useEffect(() => {
    if (!tenantId) return;
    TenantSettingsAPI.get(tenantId)
      .then((info) => {
        // Company info
        setCompanyInfo({
          company_name: info.company_name ?? '',
          company_address: info.company_address ?? '',
          company_phone: info.company_phone ?? '',
        });
        setCompanyInfoDirty(false);

        // AI config — show DB values verbatim; never paste a client-side default
        // into the textarea, otherwise the user thinks something is configured
        // when tenant_settings is actually empty (and the AI ends up using its
        // own fallback). Surface "not configured" via placeholder + helper text
        // instead of pretending the DB has a value.
        const savedWelcome = (info.welcome_message || '').trim();
        setWelcomeMessage(savedWelcome);
        setAiConfigDirty(false);
        setVoiceName((info.voice_name || '').trim());
        setTimezone((info.timezone || '').trim());
        setHoursInfo({ hours: info.business_hours || null, policy: info.after_hours_policy || null, openNow: info.open_now, sla: info.callback_sla || null });
        setOwnerNotifications(info.owner_notifications || null);
        setCompanyDescription(info.company_description || info.company_custom_knowledge || '');
      })
      .catch(() => {});
  }, [tenantId]);

  useEffect(() => {
    TenantSettingsAPI.listVoices()
      .then((resp) => {
        const options = Array.isArray(resp?.voices) ? resp.voices : [];
        setVoiceOptions(options);
      })
      .catch(() => setVoiceOptions([]));
  }, []);


  // Company Info Handlers
  const handleCompanyInfoChange = (field, value) => {
    setCompanyInfo(prev => ({ ...prev, [field]: value }));
    setCompanyInfoDirty(true);
  };

  const handleApplySuggestedTimezone = () => {
    if (!suggestedTimezone) return;
    setTimezone(suggestedTimezone);
    setAiConfigDirty(true);
    toast({ title: copy.timezoneSetFromAddress, description: `${copy.appliedTimezone} ${suggestedTimezone}`, status: 'info', duration: 2000, isClosable: true });
  };

  const handleSaveCompanyInfo = async () => {
    setCompanyInfoLoading(true);
    try {
      if (!tenantId) {
        toast({ title: copy.noTenantId, status: 'error', duration: 3000, isClosable: true });
        return false;
      }
      const payload = {
        company_name: companyInfo.company_name,
        company_address: companyInfo.company_address,
        company_phone: companyInfo.company_phone,
      };
      await TenantSettingsAPI.update(tenantId, payload);
      setCompanyInfoDirty(false);
      
      // Reload data from server to ensure consistency
      try {
        const info = await TenantSettingsAPI.get(tenantId);
        setCompanyInfo({
          company_name: info.company_name ?? '',
          company_address: info.company_address ?? '',
          company_phone: info.company_phone ?? '',
        });
      } catch (reloadErr) {
        console.warn('[Settings] Failed to reload after save:', reloadErr);
      }
      
      toast({ title: copy.companyInfoSaved, status: 'success', duration: 3000, isClosable: true });
      return true;
    } catch (e) {
      toast({ title: copy.companyInfoSaveFailed, description: e.message, status: 'error', duration: 3000, isClosable: true });
      return false;
    } finally {
      setCompanyInfoLoading(false);
    }
  };

  const parseSimpleCSV = (text) => {
    const lines = (text || '').trim().split(/\r?\n/).filter(Boolean);
    if (!lines.length) return [];
    
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      result.push(current.trim());
      return result;
    };
    
    const headers = parseCSVLine(lines[0]).map(h => h.trim());
    return lines.slice(1).map(line => {
      const values = parseCSVLine(line);
      return headers.reduce((acc, header, idx) => {
        let value = (values[idx] || '').trim();
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.slice(1, -1);
        }
        value = value.replace(/""/g, '"');
        acc[header] = value;
        return acc;
      }, {});
    });
  };

  const handleCompanyUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        if (!tenantId) {
          toast({ title: copy.importFailed, description: copy.noTenantId, status: 'error', duration: 4000, isClosable: true });
          return;
        }
        const rows = parseSimpleCSV(reader.result);
        if (!rows.length) {
          throw new Error(copy.noCsvRows);
        }
        const row = rows[0];
        setCompanyInfo({
          company_name: row.company_name || '',
          company_phone: row.company_phone || '',
          company_address: row.company_address || '',
        });
        setCompanyInfoDirty(true);
        toast({ title: copy.companyInfoImported, description: copy.saveImportedCompanyInfo, status: 'info', duration: 3000, isClosable: true });
      } catch (err) {
        toast({ title: copy.importFailed, description: err.message, status: 'error', duration: 4000, isClosable: true });
      } finally {
        if (event.target) event.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadCompanyTemplate = () => {
    const csv = [
      ['company_name', 'company_phone', 'company_address'].join(','),
      ['Bizsprite Demo Inc.', '+1 (555) 123-4567', '123 Innovation Ave, Shanghai'].join(','),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'company_info_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // AI Configuration Handlers
  const saveAiConfig = async ({ showToast = true } = {}) => {
    if (!tenantId) {
      toast({ title: copy.noTenant, status: 'error' });
      return false;
    }
    setAiConfigLoading(true);
    try {
      await TenantSettingsAPI.update(tenantId, {
        welcome_message: welcomeMessage,
        voice_name: voiceName,
        timezone: timezone,
        company_description: companyDescription,
      });
      setAiConfigDirty(false);
      if (showToast) {
        toast({ title: copy.aiSettingsSaved, status: 'success', duration: 2000 });
      }
      return true;
    } catch (e) {
      toast({ title: copy.aiSettingsSaveFailed, status: 'error' });
      return false;
    } finally {
      setAiConfigLoading(false);
    }
  };

  const openPlayground = () => {
    setPlaygroundOpen(true);
  };

  const handleStepPrimary = async () => {
    if (activeStepKey === 'profile') {
      if (companyInfoDirty) {
        const ok = await handleSaveCompanyInfo();
        if (!ok) return;
      }
      setActiveStep(1);
      return;
    }

    if (activeStepKey === 'voice') {
      if (aiConfigDirty) {
        const ok = await saveAiConfig();
        if (!ok) return;
      }
      setActiveStep(2);
      return;
    }

    if (activeStepKey === 'context') {
      if (aiConfigDirty) {
        const ok = await saveAiConfig({ showToast: false });
        if (!ok) return;
      }
      toast({
        title: copy.settingsSaved,
        description: copy.settingsSavedDetail,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const isStepSaving =
    (activeStepKey === 'profile' && companyInfoLoading) ||
    (activeStepKey === 'voice' && aiConfigLoading) ||
    (activeStepKey === 'context' && aiConfigLoading);

  const primaryButtonLabel = (() => {
    if (activeStepKey === 'profile') return companyInfoDirty ? copy.saveContinue : copy.continue;
    if (activeStepKey === 'voice') return aiConfigDirty ? copy.saveContinue : copy.continue;
    return copy.saveFinish;
  })();

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column" overflow="hidden">
      <Box flexShrink={0}>
        <PageHelp
          title={copy.pageTitle}
          description={copy.pageDescription}
        />
      </Box>

      {/* Shrinks and scrolls when the step is long, but does not stretch when it
          is short, so the step buttons sit right under the form (in the
          OneSuite embed the panel is taller than the visible window). */}
      <Box ref={stepScrollRef} flex="0 1 auto" minH={0} overflowY="auto" pb={5} onScroll={updateStepScrollHint}>
        <Card mb={4} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
          <CardBody py={4}>
            <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={4} wrap="wrap">
              <Box>
                <Badge colorScheme="blue" borderRadius="full" px={3} py={1} mb={2}>
                  {copy.step} {activeStep + 1} {copy.of} {setupSteps.length}
                </Badge>
                <Heading size="md">{setupSteps[activeStep].label}</Heading>
                <Text color="gray.600" fontSize="sm" mt={1}>{setupSteps[activeStep].description}</Text>
              </Box>
              <HStack spacing={3} align="center">
                {setupSteps.map((step, index) => {
                  const isActive = index === activeStep;
                  const isDone = index < activeStep;
                  return (
                    <Button
                      key={step.key}
                      variant="ghost"
                      size="sm"
                      px={2}
                      onClick={() => setActiveStep(index)}
                      color={isActive ? 'blue.700' : 'gray.500'}
                      aria-label={`${copy.goToStep} ${step.label}`}
                    >
                      <VStack spacing={1}>
                        <Circle
                          size="12px"
                          bg={isActive || isDone ? 'blue.500' : 'gray.300'}
                          border="2px solid"
                          borderColor={isActive ? 'blue.200' : isDone ? 'blue.500' : 'gray.300'}
                        />
                        <Text fontSize="xs" display={{ base: 'none', md: 'block' }} whiteSpace="nowrap">
                          {step.label}
                        </Text>
                      </VStack>
                    </Button>
                  );
                })}
              </HStack>
            </Flex>
          </CardBody>
        </Card>

        <Card mb={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
          <CardBody py={6}>
            {activeStepKey === 'profile' && (
              <VStack align="stretch" spacing={5}>
                <Flex justify="flex-end" gap={2} flexWrap="wrap">
                  <input
                    type="file"
                    accept=".csv"
                    ref={companyFileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleCompanyUpload}
                  />
                  <Button size="sm" leftIcon={<FiUpload />} onClick={() => companyFileInputRef.current?.click()} variant="outline">
                    {copy.importCsv}
                  </Button>
                  <Button size="sm" leftIcon={<FiDownload />} onClick={handleDownloadCompanyTemplate} variant="outline">
                    {copy.downloadTemplate}
                  </Button>
                </Flex>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold">{copy.companyName}</FormLabel>
                    <Input
                      value={companyInfo.company_name}
                      onChange={(e) => handleCompanyInfoChange('company_name', e.target.value)}
                      placeholder="Your company"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold">{copy.companyPhone}</FormLabel>
                    <Input
                      value={companyInfo.company_phone}
                      onChange={(e) => handleCompanyInfoChange('company_phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                    />
                  </FormControl>
                  <FormControl gridColumn={{ base: 'span 1', md: 'span 2' }}>
                    <FormLabel fontSize="sm" fontWeight="semibold">{copy.companyAddress}</FormLabel>
                    <Input
                      value={companyInfo.company_address}
                      onChange={(e) => handleCompanyInfoChange('company_address', e.target.value)}
                      placeholder="123 Innovation Ave, Shanghai"
                    />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            )}

            {activeStepKey === 'voice' && (
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="start">
                <Box>
                  <Text fontWeight="semibold" fontSize="md" mb={2}>{copy.welcomeMessage}</Text>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    {copy.welcomeHelp}
                  </Text>
                  <Textarea
                    value={welcomeMessage}
                    onChange={(e) => { setWelcomeMessage(e.target.value); setAiConfigDirty(true); }}
                    placeholder={copy.welcomePlaceholder}
                    rows={5}
                    maxLength={500}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {(welcomeMessage || '').length} / 500
                    {!welcomeMessage.trim() && (
                      <Text as="span" color="orange.600" ml={2}>
                        {copy.welcomeMissing}
                      </Text>
                    )}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="semibold" fontSize="md" mb={2}>{copy.aiVoice}</Text>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    {copy.voiceHelp}
                  </Text>
                  <Flex gap={3} align="center" flexWrap="wrap">
                    <Select
                      value={voiceName}
                      onChange={(e) => { setVoiceName(e.target.value); setAiConfigDirty(true); }}
                      placeholder={voiceName ? undefined : copy.voicePlaceholder}
                      maxW="320px"
                    >
                      {(voiceOptions.length ? voiceOptions : [{ value: 'Aoede', label: 'Aoede (Light/Breezy)' }]).map((v) => (
                        <option key={v.value} value={v.value}>{v.label}</option>
                      ))}
                    </Select>
                    <Button size="sm" colorScheme="purple" leftIcon={<FiPlay />} onClick={openPlayground}>
                      {copy.testVoice}
                    </Button>
                  </Flex>
                </Box>
              </SimpleGrid>
            )}

            {activeStepKey === 'context' && (
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} alignItems="start">
                <Box>
                  <Text fontWeight="semibold" fontSize="md" mb={2}>{copy.companyContext}</Text>
                  <Text fontSize="md" color="gray.700" mb={3} lineHeight="1.6">
                    {copy.companyContextHelp}
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color="blue.600" mb={3}>
                    {copy.companyContextExamples}
                  </Text>
                  <Textarea
                    value={companyDescription}
                    onChange={(e) => { setCompanyDescription(e.target.value); setAiConfigDirty(true); }}
                    placeholder={copy.companyContextPlaceholder}
                    rows={5}
                    maxLength={1000}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>{(companyDescription || '').length} / 1000</Text>
                </Box>

                <VStack align="stretch" spacing={6}>
                  <Box>
                    <Text fontWeight="semibold" fontSize="md" mb={2}>{copy.timezone}</Text>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      {copy.timezoneHelp}
                    </Text>
                    <Flex gap={3} align="center" flexWrap="wrap">
                      <Select
                        value={timezone}
                        onChange={(e) => { setTimezone(e.target.value); setAiConfigDirty(true); }}
                        placeholder={timezone ? undefined : copy.timezonePlaceholder}
                        maxW="320px"
                      >
                        {COMMON_TIMEZONES.map((tz) => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </Select>
                      {canApplySuggestedTz && (
                        <Button size="sm" variant="outline" onClick={handleApplySuggestedTimezone}>
                          {copy.applyTimezone} {suggestedTimezone}
                        </Button>
                      )}
                    </Flex>
                  </Box>

                </VStack>
              </SimpleGrid>
            )}
          </CardBody>
        </Card>

        <BusinessHoursCard tenantId={tenantId} lang={lang} initialHours={hoursInfo.hours} initialPolicy={hoursInfo.policy}
          openNow={hoursInfo.openNow} timezone={timezone || undefined} initialSla={hoursInfo.sla} />
        <OwnerNotificationsCard tenantId={tenantId} lang={lang} initial={ownerNotifications} />

      </Box>

      <Flex
        flexShrink={0}
        py={4}
        px={{ base: 4, md: 0 }}
        bg="gray.50"
        borderTop="1px"
        borderColor="gray.200"
        justify="space-between"
        align="center"
        gap={3}
        flexWrap="wrap"
      >
        <Button variant="ghost" isDisabled={activeStep === 0 || isStepSaving} onClick={() => setActiveStep((step) => Math.max(0, step - 1))}>
          {copy.back}
        </Button>
        <Box flex="1" minW={0} textAlign="center">
          {hasMoreStepContent && (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="blue"
              rightIcon={<FiChevronDown />}
              onClick={scrollStepContentDown}
            >
              {copy.moreSettings}
            </Button>
          )}
        </Box>
        <HStack spacing={3}>
          <Text fontSize="sm" color="gray.500" display={{ base: 'none', md: 'block' }}>
            {copy.step} {activeStep + 1} {copy.of} {setupSteps.length}
          </Text>
          <Button colorScheme="blue" leftIcon={<FiSave />} onClick={handleStepPrimary} isLoading={isStepSaving}>
            {primaryButtonLabel}
          </Button>
        </HStack>
      </Flex>

      <VoiceTestPlayground
        isOpen={playgroundOpen}
        onClose={() => setPlaygroundOpen(false)}
        voiceOptions={voiceOptions.length ? voiceOptions : [{ value: 'Aoede', label: 'Aoede (Light/Breezy)' }]}
        initialVoice={voiceName || 'Aoede'}
        initialText={welcomeMessage || DEFAULT_WELCOME_MESSAGE}
        tenantId={tenantId}
        onSave={(newVoice, newText) => {
          setVoiceName(newVoice);
          setWelcomeMessage(newText);
          setAiConfigDirty(true);
          toast({
            title: copy.voiceSettingsUpdated,
            description: copy.saveVoiceSettingsHelp,
            status: 'success',
            duration: 3000,
          });
        }}
      />
    </Box>
  );
};

export default SettingsPage;
